import { useState, useEffect, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Calendar } from "@/components/ui/calendar";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Separator } from "@/components/ui/separator";
import { toast } from "sonner";
import { MapPin, Clock, Loader2, ArrowLeft, Info, Share2, Bell, Timer, Zap, Layers, Dumbbell, ChevronRight } from "lucide-react";
import { format } from "date-fns";
import ShareDialog from "@/components/ShareDialog";
import { useI18n } from "@/lib/i18n";
import { KycBookingNotice } from "@/components/KycBookingNotice";
import { getBookingKycMessage, hasApprovedBookingKyc } from "@/lib/bookingKyc";
import ActivityDetailModal from "@/components/venue/ActivityDetailModal";

const sportColorMap = {
  basketball: "bg-orange-100 text-orange-700 border-orange-300",
  badminton: "bg-green-100 text-green-700 border-green-300",
  pickleball: "bg-lime-100 text-lime-700 border-lime-300",
  volleyball: "bg-blue-100 text-blue-700 border-blue-300",
  tennis: "bg-yellow-100 text-yellow-700 border-yellow-300",
  swimming: "bg-cyan-100 text-cyan-700 border-cyan-300",
  football: "bg-emerald-100 text-emerald-700 border-emerald-300",
  zumba: "bg-pink-100 text-pink-700 border-pink-300",
  taekwondo: "bg-red-100 text-red-700 border-red-300",
  yoga: "bg-purple-100 text-purple-700 border-purple-300",
  default: "bg-slate-100 text-slate-700 border-slate-300",
};

function getSportColor(sport) {
  return sportColorMap[(sport || "").toLowerCase()] || sportColorMap.default;
}

/* ── Format address, removing redundant city if it appears in address string ── */
function formatAddress(address, city) {
  if (!address) return city || "";
  if (!city) return address;
  const addr = address.trim();
  const c = city.trim();
  // Remove trailing city name (case-insensitive) and any trailing comma/space
  const pattern = new RegExp(`[,\\s]*${c.replace(/[.*+?^${}()|[\]\\]/g, "\\$&")}\\s*$`, "i");
  const cleaned = addr.replace(pattern, "").trim().replace(/,\s*$/, "");
  return cleaned ? `${cleaned}, ${c}` : c;
}

export default function VenueDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const { t } = useI18n();

  // Venue & space data
  const [venue, setVenue] = useState(null);
  const [loading, setLoading] = useState(true);
  const [hasMultiSport, setHasMultiSport] = useState(false);

  // Unified space selection
  const [unifiedSpaces, setUnifiedSpaces] = useState([]);
  const [selectedSpaceId, setSelectedSpaceId] = useState(null);
  const [selectedSpaceType, setSelectedSpaceType] = useState(null); // 'resource' | 'physical_space'

  // Sport/Activity filter
  const [sportFilter, setSportFilter] = useState("all");
  const [availableSports, setAvailableSports] = useState([]);

  // Activities (discovery products + resource-derived)
  const [activities, setActivities] = useState([]);
  const [activityModal, setActivityModal] = useState({ open: false, data: null });

  // Date & slots
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [slots, setSlots] = useState([]);
  const [slotsLoading, setSlotsLoading] = useState(false);
  const [selectedSlots, setSelectedSlots] = useState([]); // multi-slot: up to 3

  // Smart suggestions
  const [suggestions, setSuggestions] = useState([]);

  // Booking dialog
  const [hours, setHours] = useState(1);
  const [bookingDialog, setBookingDialog] = useState(false);
  const [bookingLoading, setBookingLoading] = useState(false);
  const [shareOpen, setShareOpen] = useState(false);

  // Waitlist
  const [waitlistJoining, setWaitlistJoining] = useState(false);
  const [waitlistEntry, setWaitlistEntry] = useState(null);

  // Space Engine booking
  const [seRequestNotes, setSeRequestNotes] = useState("");

  // Quick book
  const [quickBookLoading, setQuickBookLoading] = useState(null);

  /* ── Load venue data ── */
  useEffect(() => {
    api.get(`/venues/${id}`).then(r => {
      const v = r.data;
      setVenue(v);

      // Build unified spaces
      const spaces = [];
      (v.resources || []).forEach(res => {
        spaces.push({
          id: res.resource_id,
          name: res.name,
          type: "resource",
          sport_type: res.sport_type,
          surface: res.attributes?.surface || "Standard",
          indoor: res.attributes?.indoor,
          base_rate: res.pricing?.base_rate,
          capacity: res.capacity,
        });
      });
      (v.physical_spaces || []).forEach(ps => {
        spaces.push({
          id: ps.physical_space_id,
          name: ps.space_name || ps.name || "Space",
          type: "physical_space",
          classification: ps.classification,
          space_type: ps.space_type,
          surface: ps.surface_type,
          indoor: ps.is_indoor,
          dimensions: ps.dimensions_length_m && ps.dimensions_width_m ? `${ps.dimensions_length_m}m x ${ps.dimensions_width_m}m` : null,
          capacity: ps.max_safe_capacity,
        });
      });
      setUnifiedSpaces(spaces);
      if (spaces.length > 0) {
        setSelectedSpaceId(spaces[0].id);
        setSelectedSpaceType(spaces[0].type);
      }

      // Build activities from discovery_products + resources
      const acts = [];
      (v.discovery_products || []).forEach(dp => {
        acts.push({
          id: dp.discovery_product_id,
          source: "discovery_product",
          activity_name: dp.activity_name || dp.product_name,
          sport_type: dp.activity_name?.toLowerCase() || dp.marketing_tags_json?.activity?.toLowerCase(),
          space_name: dp.space_name,
          space_id: dp.physical_space_id,
          partition_name: dp.partition_name || dp.marketing_tags_json?.partition,
          product_name: dp.product_name,
          product_type: dp.product_type,
          display_from_price_amount: dp.display_from_price_amount,
          marketing_tags_json: dp.marketing_tags_json,
          booking_mode: dp.product_type,
          activity_category: dp.activity_category,
          ...dp,
        });
      });
      (v.resources || []).forEach(res => {
        // Only add resource-derived activities if not already covered by discovery products
        const hasDp = acts.some(a => a.sport_type === res.sport_type?.toLowerCase());
        if (!hasDp) {
          acts.push({
            id: res.resource_id,
            source: "resource",
            activity_name: res.sport_type,
            sport_type: res.sport_type?.toLowerCase(),
            space_name: res.name,
            space_id: res.resource_id,
            base_rate: res.pricing?.base_rate,
            capacity: res.capacity,
            booking_mode: "instant",
          });
        }
      });
      setActivities(acts);

      // Unique sports
      const sports = new Set();
      acts.forEach(a => { if (a.sport_type) sports.add(a.sport_type); });
      spaces.forEach(s => { if (s.sport_type) sports.add(s.sport_type?.toLowerCase()); });
      setAvailableSports([...sports]);

      setLoading(false);
    }).catch(() => { setLoading(false); toast.error("Venue not found"); });

    // Check if venue has flexible space engine data
    api.get(`/space-engine/venues/${id}`).then(r => {
      if (r.data?.spaces?.length > 0) setHasMultiSport(true);
    }).catch(() => {});

    // Fetch smart slot suggestions for logged-in users
    if (user) {
      api.get("/bookings/suggest-slots", { params: { venue_id: id } })
        .then(r => setSuggestions(r.data.suggestions || []))
        .catch(() => {});
    }
  }, [id, user]);

  /* ── Load slots when space selected ── */
  useEffect(() => {
    if (!selectedSpaceId || !selectedDate) return;
    setSlotsLoading(true);
    setSelectedSlots([]);
    const dateStr = format(selectedDate, "yyyy-MM-dd");

    if (selectedSpaceType === "resource") {
      api.get(`/resources/${selectedSpaceId}/slots`, { params: { date: dateStr } })
        .then(r => { setSlots(r.data.slots || []); setSlotsLoading(false); })
        .catch(() => { setSlots([]); setSlotsLoading(false); });
      if (user) {
        api.get("/waitlist/my").then(r => {
          const entry = (r.data.entries || []).find(
            e => e.resource_id === selectedSpaceId && e.desired_date === dateStr && e.status === "waiting"
          );
          setWaitlistEntry(entry || null);
        }).catch(() => setWaitlistEntry(null));
      }
    } else if (selectedSpaceType === "physical_space") {
      api.get(`/space-engine/spaces/${selectedSpaceId}/availability`, { params: { date: dateStr } })
        .then(r => { setSlots(r.data.slots || []); setSlotsLoading(false); })
        .catch(() => { setSlots([]); setSlotsLoading(false); });
    }
  }, [selectedSpaceId, selectedSpaceType, selectedDate, user]);

  const isSlotPast = useCallback((slot) => {
    const now = new Date();
    const slotStart = new Date(slot.start);
    return now >= new Date(slotStart.getTime() - 30 * 60000);
  }, []);

  const getMaxContiguousHours = useCallback((startSlot) => {
    if (!startSlot) return 1;
    const startIdx = slots.findIndex(s => s.start === startSlot.start);
    if (startIdx === -1) return 1;
    let max = 1;
    for (let i = startIdx + 1; i < slots.length && max < 3; i++) {
      const prev = new Date(slots[i - 1].end);
      const curr = new Date(slots[i].start);
      if (Math.abs(curr - prev) < 60000 && !isSlotPast(slots[i])) max++;
      else break;
    }
    return max;
  }, [slots, isSlotPast]);

  const handleSlotClick = (slot) => {
    if (isSlotPast(slot)) return;
    if (slot.available === false) return;
    if (!user) { navigate("/login"); return; }
    if (!hasApprovedBookingKyc(user)) {
      toast.error(getBookingKycMessage(user));
      navigate("/profile");
      return;
    }
    // Toggle multi-slot selection (up to 3)
    const slotKey = `${slot.start}-${slot.activity_configuration_id || ""}`;
    const exists = selectedSlots.find(s => `${s.start}-${s.activity_configuration_id || ""}` === slotKey);
    if (exists) {
      setSelectedSlots(prev => prev.filter(s => `${s.start}-${s.activity_configuration_id || ""}` !== slotKey));
    } else {
      if (selectedSlots.length >= 3) {
        toast.error("Maximum 3 slots allowed per booking");
        return;
      }
      setSelectedSlots(prev => [...prev, slot]);
    }
  };

  const handleQuickBook = async (activity) => {
    if (!user) { navigate("/login"); return; }
    if (!hasApprovedBookingKyc(user)) {
      toast.error(getBookingKycMessage(user));
      navigate("/profile");
      return;
    }

    let matchingSpace = unifiedSpaces.find(s =>
      s.type === "resource" && s.sport_type?.toLowerCase() === activity.sport_type?.toLowerCase()
    );
    if (!matchingSpace) {
      matchingSpace = unifiedSpaces.find(s =>
        s.type === "physical_space" && s.id === activity.space_id
      );
    }
    if (!matchingSpace && unifiedSpaces.length > 0) {
      matchingSpace = unifiedSpaces[0];
    }
    if (!matchingSpace) { toast.info("No bookable spaces available."); return; }

    setQuickBookLoading(activity.id);
    setSelectedSpaceId(matchingSpace.id);
    setSelectedSpaceType(matchingSpace.type);
    setSportFilter(activity.sport_type || "all");
    setSelectedSlots([]);

    setTimeout(() => {
      document.getElementById("slots-section")?.scrollIntoView({ behavior: "smooth", block: "start" });
    }, 200);

    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      let allSlots = [];
      if (matchingSpace.type === "resource") {
        const res = await api.get(`/resources/${matchingSpace.id}/slots`, { params: { date: dateStr } });
        allSlots = res.data.slots || [];
      } else {
        const res = await api.get(`/space-engine/spaces/${matchingSpace.id}/availability`, { params: { date: dateStr } });
        allSlots = res.data.slots || [];
      }
      setSlots(allSlots);

      const filtered = matchingSpace.type === "physical_space"
        ? allSlots.filter(s => s.available && s.activity_name?.toLowerCase() === activity.sport_type?.toLowerCase() && !isSlotPast(s))
        : allSlots.filter(s => !isSlotPast(s));

      if (filtered.length > 0) {
        // Auto-select first available slot
        setSelectedSlots([filtered[0]]);
        toast.success(`${activity.activity_name || "Activity"} slot selected! Pick up to 3 slots then Confirm & Pay.`);
      } else {
        toast.info("No available slots today. Try a different date.");
      }
    } catch {
      toast.error("Could not load slots.");
    }
    setQuickBookLoading(null);
  };

  const navigateToPayment = () => {
    if (selectedSlots.length === 0) return;
    const dateStr = format(selectedDate, "yyyy-MM-dd");
    const params = new URLSearchParams({
      slots: JSON.stringify(selectedSlots),
      venue_id: id,
      space_id: selectedSpaceId,
      space_name: selectedSpace?.name || "",
      space_type: selectedSpaceType,
      date: dateStr,
    });
    navigate(`/payment?${params.toString()}`);
  };

  const joinWaitlist = async () => {
    if (!user) { navigate("/login"); return; }
    setWaitlistJoining(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await api.post("/waitlist/join", { resource_id: selectedSpaceId, desired_date: dateStr, desired_start_time: "08:00", desired_end_time: "22:00" });
      setWaitlistEntry(res.data);
      toast.success("Added to waitlist!");
    } catch (err) { toast.error(err.response?.data?.detail || "Could not join waitlist"); }
    setWaitlistJoining(false);
  };

  const leaveWaitlist = async () => {
    if (!waitlistEntry) return;
    try { await api.delete(`/waitlist/${waitlistEntry.waitlist_id}`); setWaitlistEntry(null); toast.success("Removed from waitlist"); } catch { toast.error("Failed"); }
  };

  if (loading) return <div className="flex justify-center py-20"><Loader2 className="w-8 h-8 animate-spin text-court-orange" /></div>;
  if (!venue) return <div className="text-center py-20 font-heading text-xl">Venue not found</div>;

  const selectedSpace = unifiedSpaces.find(s => s.id === selectedSpaceId);
  const isToday = format(selectedDate, "yyyy-MM-dd") === format(new Date(), "yyyy-MM-dd");
  const kycBlocked = user && !hasApprovedBookingKyc(user);
  const kycMessage = getBookingKycMessage(user);
  const displayAddress = formatAddress(venue.address, venue.city);
  const selectedSlotsTotal = selectedSlots.reduce((sum, s) => sum + (s.rate || 0), 0);

  // Filter activities by selected sport
  const filteredActivities = sportFilter === "all" ? activities : activities.filter(a => a.sport_type === sportFilter);

  // Filter slots by sport filter for SE spaces (show all for legacy resources)
  const filteredSlots = (() => {
    if (sportFilter === "all") return slots;
    if (selectedSpaceType === "physical_space") {
      return slots.filter(s => s.activity_name?.toLowerCase() === sportFilter);
    }
    return slots;
  })();

  return (
    <div className="max-w-6xl mx-auto px-4 md:px-8 py-6">
      <button data-testid="back-btn" onClick={() => navigate(-1)} className="flex items-center gap-2 text-sm text-muted-foreground hover:text-foreground mb-4">
        <ArrowLeft className="w-4 h-4" />{t("venue.back")}
      </button>

      {/* ── Venue Header ── */}
      <div className="relative h-56 md:h-72 overflow-hidden mb-6 border-b-4 border-r-4 border-black">
        <img src={venue.images?.[0] || "https://images.unsplash.com/photo-1546519638-68e109498ffc?w=1000"} alt={venue.name} className="w-full h-full object-cover" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/60 to-transparent" />
        <div className="absolute bottom-4 left-4 md:bottom-6 md:left-6">
          <div className="flex gap-2 mb-2">
            {venue.sport_types?.map(s => (
              <Badge key={s} className={`${getSportColor(s)} text-[10px] font-heading uppercase border`}>{s}</Badge>
            ))}
          </div>
          <h1 className="font-heading text-2xl md:text-4xl text-white font-bold uppercase tracking-tight" data-testid="venue-name">{venue.name}</h1>
          <p className="flex items-center gap-1 text-white/70 text-sm mt-1" data-testid="venue-address">
            <MapPin className="w-3 h-3" />{displayAddress}
          </p>
        </div>
        <Button data-testid="share-venue-btn" onClick={() => setShareOpen(true)} className="absolute top-4 right-4 bg-white/20 hover:bg-white/30 text-white backdrop-blur-sm rounded-full w-10 h-10 p-0">
          <Share2 className="w-5 h-5" />
        </Button>
      </div>

      {venue.description && <p className="text-muted-foreground mb-6">{venue.description}</p>}

      {/* ── Explore this Venue banner (multi-sport) ── */}
      {hasMultiSport && (
        <div className="mb-6 bg-gradient-to-r from-primary/10 to-blue-50 border-2 border-primary/20 rounded-sm p-4 flex items-center justify-between" data-testid="multisport-banner">
          <div>
            <p className="font-heading text-sm font-bold uppercase text-primary">Multi-Sport Venue</p>
            <p className="text-xs text-muted-foreground">This venue supports multiple activities with flexible court configurations</p>
          </div>
          <Button onClick={() => navigate(`/venues/${id}/multisport`)} variant="outline" className="border-primary text-primary hover:bg-primary/10" data-testid="explore-venue-btn">
            Explore this Venue
          </Button>
        </div>
      )}

      {/* ── Available Sports / Activities ── */}
      {activities.length > 0 && (
        <div className="mb-8" data-testid="activities-section">
          <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-3">Available Sports / Activities</h3>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {filteredActivities.map((act) => {
              const sport = act.activity_name || act.sport_type || "Activity";
              const price = act.display_from_price_amount || act.base_rate;
              const colorCls = getSportColor(act.sport_type);
              const isLoading = quickBookLoading === act.id;
              return (
                <div
                  key={act.id}
                  data-testid={`activity-tile-${act.id}`}
                  className="border-2 border-slate-200 hover:border-primary/40 rounded-sm p-4 transition-all hover:-translate-y-0.5 group"
                >
                  <button
                    className="w-full text-left"
                    onClick={() => setActivityModal({ open: true, data: act })}
                    data-testid={`activity-info-${act.id}`}
                  >
                    <div className="flex items-start justify-between mb-2">
                      <h4 className="font-heading text-lg font-bold uppercase tracking-tight text-foreground group-hover:text-primary transition-colors" data-testid={`activity-name-${act.id}`}>
                        {sport}
                      </h4>
                      <ChevronRight className="w-4 h-4 text-muted-foreground group-hover:text-primary shrink-0 mt-1" />
                    </div>
                    {act.space_name && (
                      <p className="text-xs text-muted-foreground mb-1">{act.space_name}{act.partition_name ? ` \u00b7 ${act.partition_name}` : ""}</p>
                    )}
                  </button>
                  <div className="flex items-center justify-between mt-3">
                    <div className="flex items-center gap-2">
                      {price && (
                        <Badge className={`${colorCls} border text-xs font-heading`}>
                          {"\u20B1"}{Number(price).toLocaleString()}/hr
                        </Badge>
                      )}
                      {act.booking_mode && (
                        <Badge variant="outline" className="text-[10px] capitalize">
                          {act.booking_mode === "instant" || act.booking_mode === "instant_book" ? "Instant" : act.booking_mode.replace(/_/g, " ")}
                        </Badge>
                      )}
                    </div>
                    <button
                      data-testid={`quick-book-${act.id}`}
                      onClick={() => handleQuickBook(act)}
                      disabled={isLoading}
                      className="flex items-center gap-1.5 px-3 py-1.5 rounded-full text-xs font-heading uppercase tracking-wide transition-all bg-orange-500 text-white hover:bg-orange-600 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:shadow-none active:translate-x-[2px] active:translate-y-[2px]"
                    >
                      {isLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Zap className="w-3 h-3" />}
                      Book Now
                    </button>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        {/* ── Left: Space Tiles + Filters + Calendar ── */}
        <div className="lg:col-span-1 space-y-6">
          {/* Select Space */}
          <div>
            <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-3">Select Space</h3>
            {unifiedSpaces.length === 0 ? (
              <p className="text-sm text-muted-foreground">No spaces available</p>
            ) : (
              <div className="space-y-2" data-testid="space-tiles">
                {unifiedSpaces.map(space => {
                  const isSelected = selectedSpaceId === space.id;
                  return (
                    <button
                      key={space.id}
                      data-testid={`space-tile-${space.id}`}
                      onClick={() => { setSelectedSpaceId(space.id); setSelectedSpaceType(space.type); setSelectedSlots([]); }}
                      className={`w-full text-left p-3 border-2 rounded-sm transition-all ${isSelected ? "border-primary bg-primary/5 ring-1 ring-primary/20" : "border-slate-200 hover:border-slate-300 hover:-translate-y-0.5"}`}
                    >
                      <div className="flex items-center justify-between">
                        <div className="flex items-center gap-2">
                          <Layers className={`w-4 h-4 shrink-0 ${isSelected ? "text-primary" : "text-muted-foreground"}`} />
                          <span className={`font-heading text-sm font-bold uppercase tracking-tight ${isSelected ? "text-primary" : "text-foreground"}`}>
                            {space.name}
                          </span>
                        </div>
                        {space.sport_type && (
                          <Badge className={`${getSportColor(space.sport_type)} border text-[10px]`}>{space.sport_type}</Badge>
                        )}
                        {space.classification && !space.sport_type && (
                          <Badge variant="outline" className="text-[10px] capitalize">{space.classification.replace(/_/g, " ")}</Badge>
                        )}
                      </div>
                      <div className="flex items-center gap-3 mt-1.5 text-xs text-muted-foreground">
                        {space.surface && <span>{space.surface}</span>}
                        {space.indoor !== undefined && <span>{space.indoor ? "Indoor" : "Outdoor"}</span>}
                        {space.dimensions && <span>{space.dimensions}</span>}
                        {space.capacity && <span>{space.capacity} pax</span>}
                        {space.base_rate && <span className="font-medium text-foreground">{"\u20B1"}{space.base_rate}/hr</span>}
                      </div>
                    </button>
                  );
                })}
              </div>
            )}
          </div>

          {/* Sport/Activity Filter */}
          {availableSports.length > 1 && (
            <div>
              <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-2">Select Sport / Activity</h3>
              <Select value={sportFilter} onValueChange={setSportFilter} data-testid="sport-filter-select">
                <SelectTrigger className="h-10 rounded-sm border-2" data-testid="sport-filter-trigger">
                  <SelectValue placeholder="All Sports" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Sports</SelectItem>
                  {availableSports.map(s => (
                    <SelectItem key={s} value={s} className="capitalize">{s}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Calendar */}
          <div>
            <h3 className="font-heading text-sm uppercase tracking-[0.15em] text-muted-foreground mb-2">{t("venue.selectDate")}</h3>
            <Calendar
              mode="single"
              selected={selectedDate}
              onSelect={d => { if (d) { setSelectedDate(d); setSelectedSlots([]); } }}
              disabled={(date) => date < new Date(new Date().setHours(0, 0, 0, 0))}
              className="border-2 border-slate-200 p-3"
              classNames={{ day_today: "bg-orange-100 text-orange-700 font-bold" }}
              data-testid="date-calendar"
            />
          </div>
        </div>

        {/* ── Right: Slots ── */}
        <div className="lg:col-span-2" id="slots-section">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-heading text-lg uppercase tracking-tight font-bold">
              {t("venue.availableSlots")} - {format(selectedDate, "MMM d, yyyy")}
            </h3>
            {selectedSpace?.base_rate && (
              <div className="flex items-center gap-1 text-sm text-muted-foreground">
                <Info className="w-3 h-3" />{t("venue.from")} {"\u20B1"}{selectedSpace.base_rate?.toFixed(0)}{t("venue.perHour")}
              </div>
            )}
          </div>

          {/* Smart Suggestions Strip */}
          {suggestions.length > 0 && filteredSlots.length > 0 && (
            <div className="mb-4 bg-orange-50 border-2 border-orange-200 rounded-sm p-3" data-testid="smart-suggestions">
              <p className="text-xs font-heading uppercase tracking-[0.15em] text-orange-600 mb-2 flex items-center gap-1">
                <Zap className="w-3 h-3" />Recommended For You
              </p>
              <div className="flex gap-2 overflow-x-auto pb-1">
                {suggestions.map((sug, i) => {
                  const matchingSlot = filteredSlots.find(s => {
                    const h = parseInt(s.start?.slice(11, 13) || "0");
                    return h === sug.start_hour && !isSlotPast(s) && s.available !== false;
                  });
                  if (!matchingSlot) return null;
                  return (
                    <button key={i} data-testid={`suggestion-${i}`}
                      onClick={() => handleSlotClick(matchingSlot)}
                      className="flex-shrink-0 bg-white border border-orange-200 rounded-sm px-3 py-2 text-left hover:border-orange-400 transition-colors">
                      <p className="font-heading text-sm font-bold text-orange-700">{sug.label}</p>
                      <p className="text-[10px] text-muted-foreground">{sug.reason}</p>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {kycBlocked && (
            <div className="mb-4">
              <KycBookingNotice message={kycMessage} compact testId="venue-detail-kyc-notice" />
            </div>
          )}

          {/* Multi-select hint */}
          {filteredSlots.length > 0 && !kycBlocked && user && (
            <p className="text-xs text-muted-foreground mb-3 flex items-center gap-1" data-testid="multi-select-hint">
              <Info className="w-3 h-3" />Tap slots to select up to 3. Selected: <strong className="text-primary">{selectedSlots.length}/3</strong>
            </p>
          )}

          {slotsLoading ? (
            <div className="flex justify-center py-12"><Loader2 className="w-6 h-6 animate-spin text-court-orange" /></div>
          ) : filteredSlots.length === 0 ? (
            <div className="text-center py-10 border-2 border-dashed border-slate-200 space-y-4" data-testid="no-slots-waitlist">
              <Clock className="w-8 h-8 text-muted-foreground mx-auto" />
              <div>
                <p className="font-heading text-muted-foreground uppercase">{t("venue.noSlots")}</p>
                <p className="text-sm text-muted-foreground mt-1">{t("venue.allBooked")}</p>
              </div>
              {user && selectedSpaceType === "resource" && !waitlistEntry && (
                <Button onClick={joinWaitlist} disabled={waitlistJoining} className="rounded-full bg-primary text-white" data-testid="join-waitlist-btn">
                  <Bell className="w-4 h-4 mr-2" />{waitlistJoining ? "Joining..." : t("venue.joinWaitlist")}
                </Button>
              )}
              {user && waitlistEntry && (
                <div className="bg-primary/5 border border-primary/20 rounded-sm p-4 max-w-xs mx-auto" data-testid="waitlist-active">
                  <p className="text-sm font-medium text-primary">{t("venue.onWaitlist")} #{waitlistEntry.position}</p>
                  <Button variant="ghost" size="sm" onClick={leaveWaitlist} className="mt-1 text-xs text-destructive" data-testid="leave-waitlist-btn">{t("venue.leaveWaitlist")}</Button>
                </div>
              )}
              {!user && <p className="text-xs text-muted-foreground"><button onClick={() => navigate("/login")} className="text-primary underline">{t("nav.login")}</button> {t("venue.loginToWaitlist")}</p>}
            </div>
          ) : (
            <div className="grid grid-cols-3 sm:grid-cols-4 md:grid-cols-5 gap-2" data-testid="slots-grid">
              {filteredSlots.map((slot, i) => {
                const startH = slot.start?.slice(11, 16) || "";
                const endH = slot.end?.slice(11, 16) || "";
                const slotKey = `${slot.start}-${slot.activity_configuration_id || ""}`;
                const isSelected = selectedSlots.some(s => `${s.start}-${s.activity_configuration_id || ""}` === slotKey);
                const past = isToday && isSlotPast(slot);
                const unavailable = slot.available === false;
                const isRequest = slot.booking_mode === "request_only";
                const rate = slot.rate || 0;
                return (
                  <button key={`${slot.start}-${slot.activity_configuration_id || i}`} data-testid={`slot-${i}`}
                    disabled={past || kycBlocked || unavailable}
                    onClick={() => handleSlotClick(slot)}
                    className={`slot-btn text-center transition-all relative ${isSelected ? "selected ring-2 ring-orange-500 bg-orange-50" : ""} ${slot.is_peak ? "peak" : ""} ${past || kycBlocked || unavailable ? "opacity-30 cursor-not-allowed" : "hover:-translate-y-0.5"}`}>
                    {isSelected && <div className="absolute top-1 right-1 w-3 h-3 rounded-full bg-orange-500 text-white text-[8px] flex items-center justify-center font-bold">{selectedSlots.findIndex(s => `${s.start}-${s.activity_configuration_id || ""}` === slotKey) + 1}</div>}
                    <div className="font-heading text-sm font-bold">{startH}</div>
                    <div className="text-[10px] text-muted-foreground">{endH}</div>
                    {rate > 0 && <div className="font-bold text-xs mt-1">{"\u20B1"}{rate.toFixed ? rate.toFixed(0) : rate}</div>}
                    {slot.activity_name && <div className="text-[8px] text-muted-foreground truncate mt-0.5">{slot.activity_name}</div>}
                    {slot.is_peak && <span className="text-[8px] text-amber-600 font-heading uppercase">{t("venue.peak")}</span>}
                    {isRequest && <span className="text-[8px] text-blue-600 font-heading">REQUEST</span>}
                    {past && <span className="text-[8px] text-red-400 font-heading">PAST</span>}
                    {unavailable && slot.reason && <span className="text-[8px] text-red-400">{slot.reason}</span>}
                  </button>
                );
              })}
            </div>
          )}
        </div>
      </div>

      {/* ── Floating Confirm Bar ── */}
      {selectedSlots.length > 0 && (
        <div className="fixed bottom-0 left-0 right-0 bg-white border-t-2 border-black shadow-[0_-4px_12px_rgba(0,0,0,0.1)] z-50 px-4 py-3" data-testid="confirm-bar">
          <div className="max-w-4xl mx-auto flex items-center justify-between">
            <div>
              <p className="text-sm font-heading uppercase tracking-tight font-bold">{selectedSlots.length} slot{selectedSlots.length > 1 ? "s" : ""} selected</p>
              <p className="text-xs text-muted-foreground">
                {selectedSlots.map(s => s.start?.slice(11, 16)).join(", ")} {"\u00b7"} Total: <span className="font-bold text-foreground">{"\u20B1"}{selectedSlotsTotal.toLocaleString()}</span>
              </p>
            </div>
            <div className="flex items-center gap-2">
              <Button variant="ghost" size="sm" onClick={() => setSelectedSlots([])} className="text-xs" data-testid="clear-selection-btn">Clear</Button>
              <Button data-testid="confirm-pay-btn" onClick={navigateToPayment}
                className="bg-orange-500 hover:bg-orange-600 text-white rounded-full font-heading uppercase tracking-wide px-6 h-10 shadow-[2px_2px_0px_0px_rgba(0,0,0,1)] active:shadow-none">
                Confirm & Pay {"\u20B1"}{selectedSlotsTotal.toLocaleString()}
              </Button>
            </div>
          </div>
        </div>
      )}

      {/* ── Activity Detail Modal ── */}
      <ActivityDetailModal open={activityModal.open} onOpenChange={(v) => setActivityModal({ ...activityModal, open: v })} activity={activityModal.data} />

      <ShareDialog open={shareOpen} onOpenChange={setShareOpen}
        shareUrl={`${window.location.origin}/venues/${id}`}
        title={`Book at ${venue.name}`}
        description={`${venue.sport_types?.join(", ")} courts in ${venue.city}. Book your slot now!`} />

      {/* Bottom padding for floating bar */}
      {selectedSlots.length > 0 && <div className="h-20" />}
    </div>
  );
}
